import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as configActions, selectors as configSelectors, Configuration } from 'store/configurations';
import { getSortedList, getUniqueName } from 'utils/stringFormat';
import { useStyles } from '../../styled';
import { ManageConfigurationsOverlayProps } from '../types';
import CreateConfigurationOverlay from './CreateConfigurationOverlay';

const ManageConfigurationsOverlay = ({ onClose }: ManageConfigurationsOverlayProps) => {
  const classes = useStyles();

  const { getConfigurations, createConfiguration, deleteConfiguration } = useActions({
    getConfigurations: configActions.getConfigurations,
    createConfiguration: configActions.createConfiguration,
    deleteConfiguration: configActions.deleteConfiguration
  });
  const configurations = useSelector(configSelectors.selectConfigurations);
  const { pending } = useSelector(configSelectors.selectAction);

  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [configurationToDelete, setConfigurationToDelete] = useState<Configuration | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);

  const sortedConfigurations = useMemo(() => {
    const sorted = getSortedList(configurations, 'name');
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [configurations, sortOrder]);

  // Refresh the list once a create or delete request finishes. Errors are shown by ConfigurationsView.
  useEffect(() => {
    if (uiLoading && !pending) {
      setShowCreateOverlay(false);
      setUILoading(false);
      getConfigurations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiLoading, pending]);

  const onCreateSubmit = (name: string) => {
    setUILoading(true);
    createConfiguration({ name });
  };

  const onCopyClick = (configuration: Configuration) => {
    setUILoading(true);
    createConfiguration({
      name: getUniqueName(configuration.name, configurations),
      ticketing: configuration.ticketing,
      willCall: configuration.willCall,
      messaging: configuration.messaging,
      infoView: configuration.infoView,
      configuration: configuration.configuration,
      cityPass: configuration.cityPass,
      souvenir: configuration.souvenir
    });
  };

  const onDeleteConfirmClick = () => {
    if (configurationToDelete) {
      setUILoading(true);
      deleteConfiguration({ id: configurationToDelete.id });
      setConfigurationToDelete(null);
    }
  };

  return (
    <FormDialog
      open
      title="Configurations"
      cancelButtonAction={onClose}
      confirmButtonText="Close"
      confirmButtonAction={onClose}
      headerButtonAction={() => setShowCreateOverlay(true)}
      headerButtonText="Create Configuration"
    >
      <MuiBox py="16px">
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer>
              <MuiTable.Table stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      className={classes.tableHeader}
                      sx={{ width: '440px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      Name
                      <SvgIcon
                        name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'}
                        size={15}
                        style={{ marginLeft: '5px' }}
                      />
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.tableHeader} sx={{ textAlign: 'center' }}>
                      Actions
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {sortedConfigurations.map((configuration: Configuration) => (
                    <MuiTable.TableRow key={configuration.id}>
                      <MuiTable.TableCell>{configuration.name}</MuiTable.TableCell>
                      <MuiTable.TableCell align="center">
                        <MuiBox display="flex" gap="10px">
                          <Tooltip title="Copy Configuration" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => onCopyClick(configuration)}
                              data-testid="copy-configuration-button"
                            >
                              <SvgIcon name="copy_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          <Tooltip title="Delete Configuration" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => setConfigurationToDelete(configuration)}
                              data-testid="delete-configuration-button"
                            >
                              <SvgIcon name="exit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                        </MuiBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>
      {configurationToDelete && (
        <FormDialog
          open
          confirmButtonAction={onDeleteConfirmClick}
          confirmButtonText="Delete"
          cancelButtonAction={() => setConfigurationToDelete(null)}
          cancelButtonText="Cancel"
        >
          <Typography fontSize={24}>Are you sure you want to delete this configuration?</Typography>
        </FormDialog>
      )}
      {showCreateOverlay && (
        <CreateConfigurationOverlay onClose={() => setShowCreateOverlay(false)} onSubmit={onCreateSubmit} />
      )}
    </FormDialog>
  );
};

export default ManageConfigurationsOverlay;
